/**
 * AddSupplierCodeDialog — "Add new supplier code" form (v1.19).
 *
 * The supplier-side mirror of AddProductDialog. Creates a price-book entry for
 * the current supplier (code + supplier description + cost) that is intentionally
 * UNLINKED to an internal code — modelling the state "I have a supplier code that
 * isn't associated with an internal code yet". The Code cell then handles linking
 * (match existing / create new) later.
 *
 * Supplier is shown read-only, exactly like AddProductDialog, so the two create
 * flows feel identical.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Box, Typography, InputAdornment, Chip,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { BRAND, BRAND_HOVER, FI_ORANGE } from '../theme/tokens';

export interface SupplierCodeDraft {
  supplierCode: string;
  description: string;
  unitPrice: number;
}

export interface AddSupplierCodeDialogProps {
  open: boolean;
  supplierName: string;
  prefill: { supplierCode?: string; description?: string; unitPrice?: number };
  onClose: () => void;
  onSave: (draft: SupplierCodeDraft, addAnother: boolean) => void;
}

const fieldSx = { '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } };

export const AddSupplierCodeDialog: React.FC<AddSupplierCodeDialogProps> = ({
  open, supplierName, prefill, onClose, onSave,
}) => {
  const [supplierCode, setSupplierCode] = useState('');
  const [description, setDescription] = useState('');
  const [unitPrice, setUnitPrice] = useState('0');

  useEffect(() => {
    if (!open) return;
    setSupplierCode(prefill.supplierCode ?? '');
    setDescription(prefill.description ?? '');
    setUnitPrice(prefill.unitPrice != null ? String(prefill.unitPrice) : '0');
  }, [open, prefill.supplierCode, prefill.description, prefill.unitPrice]);

  const canSave = supplierCode.trim().length > 0 && description.trim().length > 0;
  const build = (): SupplierCodeDraft => ({
    supplierCode: supplierCode.trim().toUpperCase(),
    description: description.trim(),
    unitPrice: parseFloat(unitPrice) || 0,
  });
  const save = (addAnother: boolean) => {
    if (!canSave) return;
    onSave(build(), addAnother);
    if (addAnother) { setSupplierCode(''); setDescription(''); setUnitPrice('0'); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '1.05rem' }}>
        <LocalOfferIcon sx={{ color: BRAND }} /> Add supplier code
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box sx={{ backgroundColor: '#f7f4fc', border: '1px solid #e4d4f5', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
              Supplier
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: BRAND }}>{supplierName}</Typography>
              <Chip label="Defaulted · not editable" size="small"
                sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#ede7f6', color: BRAND }} />
            </Stack>
          </Box>

          <TextField
            label="Supplier code" value={supplierCode} onChange={e => setSupplierCode(e.target.value.toUpperCase())}
            size="small" fullWidth required autoFocus sx={fieldSx}
            helperText="The supplier's own part number — printed on the PO"
          />
          <TextField
            label="Supplier description" value={description} onChange={e => setDescription(e.target.value)}
            size="small" fullWidth required multiline minRows={2} sx={fieldSx}
            helperText="As it should appear on the PO sent to the supplier"
          />
          <TextField
            label="Supplier cost (ex GST)" value={unitPrice} onChange={e => setUnitPrice(e.target.value)}
            size="small" type="number" sx={{ ...fieldSx, width: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />

          <Box sx={{ backgroundColor: '#fff8f0', border: '1px solid #ffe0b2', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#8a6d3b' }}>
              Added to <strong>{supplierName}</strong>'s price book without an inventory code.
              Use <strong>Auto-set</strong> on the Inventory Code column once the supplier code is set
              (creates a company code like FIE-234).
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        <Button onClick={() => save(true)} disabled={!canSave}
          sx={{ textTransform: 'none', color: FI_ORANGE, fontWeight: 700 }}>
          Save and add another
        </Button>
        <Button onClick={() => save(false)} disabled={!canSave} variant="contained"
          sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: BRAND_HOVER } }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSupplierCodeDialog;
